import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Modal from "react-modal";
import PaymentLinkTable from "../../PaymentLinkTable";
import PaymentInfoMobile from "../../open-partial-link/PaymentInfoMobile";
import CashVerificationQR from "../../CashVerificationQR";
import ModalHeadAndClose from "../../../payouts-wtf/common/ModalHeadAndClose";
import { collectPartialPayment } from "../../../../redux/reducers/postApiSlice";
import {
  showConfirmAlert,
  showInputAlert,
} from "../../../components-react/ReactAlert";
import {
  convertStraight,
  convertThenFormat,
  formatDateTimezon,
} from "../../../common-data/Converts";

const OnlineCashCollect = ({
  tbData,
  userInfoData,
  callFunction,
  cashSuccessMessage,
}) => {
  const dispatch = useDispatch();

  // QR verification modal state. cashDraftId is set after the Stage-1 backend
  // call returns requires_verification=true; pendingPaymentUid is the payment
  // row we'll finalize once the QR is scanned + verified (Stage-2).
  const [showCashQR, setShowCashQR] = useState(false);
  const [cashDraftId, setCashDraftId] = useState(null);
  const [pendingPaymentUid, setPendingPaymentUid] = useState(null);

  const closeCashQR = () => {
    setShowCashQR(false);
    setCashDraftId(null);
    setPendingPaymentUid(null);
  };

  const onClickActionBtn = (e, data, name) => {
    e.preventDefault();

    const paymentUid = data?.uid;
    if (!paymentUid) {
      toast.error("Missing payment id for this row");
      return;
    }

    if (name === "cash") {
      showConfirmAlert(
        () =>
          submitCollection(
            { payment_uid: paymentUid, mode: "cash" },
            "cash"
          ),
        `Confirm ₹${data?.amount} cash received?`,
        "No, Cancel",
        "Yes, Mark Received"
      );
      return;
    }

    if (name === "online") {
      showInputAlert({
        title: `Collect ₹${data?.amount} Online`,
        infoTxt: "Enter Transaction ID (UPI ref / GPay ID / TXN number)",
        placeholder: "e.g. 4342 or TXN123456",
        minLength: 4,
        submitTxt: "Mark Received",
        cancelTxt: "Cancel",
        onSubmit: (trxId) =>
          submitCollection(
            { payment_uid: paymentUid, mode: "online", trx_id: trxId },
            "online"
          ),
      });
    }
  };

  const submitCollection = async (userData, name) => {
    try {
      const result = await dispatch(
        collectPartialPayment({ userData, TOKEN: userInfoData?.token })
      );

      // Stage-1 success for cash: backend minted a draft + wants QR verification
      // before recording the payment. Pop the QR modal and wait for the member
      // to scan + submit photo on mobile.
      if (
        result?.payload?.status &&
        result?.payload?.requires_verification &&
        result?.payload?.cash_draft_id
      ) {
        setCashDraftId(result.payload.cash_draft_id);
        setPendingPaymentUid(userData.payment_uid);
        setShowCashQR(true);
        return;
      }

      if (result?.payload?.status) {
        const successMsg =
          name === "cash" && cashSuccessMessage
            ? cashSuccessMessage
            : result?.payload?.message || "Payment recorded ✅";
        toast.success(successMsg);
        callFunction();
      } else {
        const errorMessage =
          result?.payload?.message ||
          result?.error?.message ||
          "Something Went Wrong!";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.log("submitCollection ERROR => ", error);
      toast.error("Failed to record payment. Please retry.");
    }
  };

  // Stage-2: invoked by <CashVerificationQR> once cash_verification_sessions
  // flips to status='verified'. Re-hit the same endpoint with the verification
  // token + draft id so the backend can finalize the payment row.
  const handleCashVerified = async (verificationToken /*, verificationData*/) => {
    if (!pendingPaymentUid || !cashDraftId) return;
    try {
      const result = await dispatch(
        collectPartialPayment({
          userData: {
            payment_uid: pendingPaymentUid,
            mode: "cash",
            cash_verified: true,
            cash_verification_token: verificationToken,
            cash_draft_id: cashDraftId,
          },
          TOKEN: userInfoData?.token,
        })
      );

      if (result?.payload?.status) {
        toast.success(
          cashSuccessMessage ||
            result?.payload?.message ||
            "Cash payment recorded ✅"
        );
        closeCashQR();
        callFunction();
      } else {
        toast.error(
          result?.payload?.message ||
            result?.error?.message ||
            "Failed to finalize cash payment"
        );
      }
    } catch (error) {
      console.log("handleCashVerified ERROR => ", error);
      toast.error("Failed to finalize cash payment. Please retry.");
    }
  };

  const pmInfoColumns = [
    {
      dataField: "payment_created",
      text: "Payment Created",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.payment_created
              ? formatDateTimezon(row.payment_created)
              : "-"}
          </span>
        );
      },
    },
    {
      dataField: "date",
      text: "Schedule Date",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.date ? convertStraight(row.date) : "-"}
          </span>
        );
      },
    },
    {
      dataField: "amount",
      text: "Amount",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            ₹{convertThenFormat(row?.amount || 0)}
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (_, row) => {
        return (
          <span
            className="tb-data"
            style={{
              background: row?.status === "completed" ? "#379C00" : "#CD0909",
              color: "#fff",
              padding: "5px 10px",
              borderRadius: "4px",
              letterSpacing: "1px",
            }}>
            <small>{row?.status || "NA"}</small>
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: "Action",
      formatter: (_, row) => {
        return row?.status === "completed" ? (
          <span className="tb-data icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none">
              <path
                d="M10 1.875C5.51172 1.875 1.875 5.51172 1.875 10C1.875 14.4883 5.51172 18.125 10 18.125C14.4883 18.125 18.125 14.4883 18.125 10C18.125 5.51172 14.4883 1.875 10 1.875ZM8.74609 12.8789C8.65234 12.9727 8.51953 13.0508 8.40234 13.0508C8.28516 13.0508 8.15234 12.9688 8.05469 12.875L5.86719 10.6875L6.5625 9.99219L8.40625 11.8359L13.2812 6.92578L13.9648 7.63281L8.74609 12.8789Z"
                fill="#379C00"
              />
            </svg>
          </span>
        ) : (
          <div className="actionButtonContainer">
            <button
              className="actionBtn"
              onClick={(e) => onClickActionBtn(e, row, "cash")}
              style={{ background: "#F3DADA", color: "#D90000" }}>
              Collect Cash
            </button>
            <button
              className="actionBtn"
              onClick={(e) => onClickActionBtn(e, row, "online")}
              style={{ background: "#E1EAF3", color: "#3474F1" }}>
              Collect Online
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="fullWidthFlexCenter overflowAuto separatedTable hideMobile">
        <PaymentLinkTable tableData={tbData} tableColumns={pmInfoColumns} />
      </div>

      <div className="lead-logs-card-container only-mobile">
        <PaymentInfoMobile
          tableData={tbData}
          onClickActionBtn={onClickActionBtn}
        />
      </div>

      <Modal
        isOpen={showCashQR}
        onRequestClose={closeCashQR}
        contentLabel="Scan QR to Verify Cash"
        ariaHideApp={false}>
        <div className="createNewCTOModalContainer">
          <ModalHeadAndClose title="Scan QR to verify cash" onClose={closeCashQR} />
          <div style={{ padding: "20px" }}>
            {cashDraftId && (
              <CashVerificationQR
                cashDraftId={cashDraftId}
                onVerified={handleCashVerified}
              />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default OnlineCashCollect;
